import {
  Component,
  ElementRef,
  Renderer2,
  AfterViewInit,
  Input,
  ViewChild,
  ViewChildren,
  QueryList,
  ViewEncapsulation,
  OnChanges,
  ChangeDetectionStrategy,
  Inject,
  LOCALE_ID,
} from '@angular/core';
import { addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Options, SeriesOptionsType } from 'highcharts';
import * as Highcharts from 'highcharts/highcharts';
import { merge, uniq, isPlainObject, isArray, cloneDeep } from 'lodash';
import { ColorService, FormatPipe, DatesService, SessionService, CurrencyPipe, ChartValueColor } from '@hub-components';

import {
  DEFAULT_TITLE_FONT_SIZE,
  DEFAULT_AXIS_FONT_SIZE,
  DEFAULT_LEGEND_FONT_SIZE,
  DEFAULT_DATA_LABEL_FONT_SIZE,
} from '../../../../constants';

import { WidgetService } from '../../../dashboards/services/widget.service';
import { WidgetStyle } from '../../interfaces/widget-style.interface';
import { ChartOptions } from '../chart-options';

const HIGHCHARTS_OPTIONS: Partial<Options> = {
  chart: {
    animation: false,
    style: {
    	color: '#333333',
      fontFamily: 'Arial, Helvetica, sans-serif',
      fontSize: '14px',
    },
    backgroundColor: 'transparent',
  },
  credits: {
    enabled: false,
  },
  legend: {
    enabled: true,
    itemStyle: {
    	fontWeight: '500',
      fontSize: DEFAULT_LEGEND_FONT_SIZE + 'px',
      color: '#333333',
    },
    symbolWidth: 10,
    symbolHeight: 10,
    symbolRadius: 5,
  },
  title: null,
  xAxis: [
    {
      title: null,
      type: 'datetime',
      labels: {
        style: {
          color: '#333333',
        },
      },
      lineColor: 'rgba(158, 158, 158, 0.5)',
      tickLength: 6,
    },
  ],
  yAxis: [
    {
      title: null,
      labels: {
        style: {
          color: '#333333',
        },
      },
      tickColor: 'rgba(158, 158, 158, 0.5)',
    },
  ],
  plotOptions: {
    series: {
    	animation: false,
      lineWidth: 3,
      marker: {
        enabled: false,
        symbol: 'circle',
        radius: 2,
        lineColor: null,
      },
      states: {
        hover: {
          lineWidthPlus: 0,
          halo: {
            size: 0,
          },
        },
      },
    },
    column: {
      groupPadding: 0.09,
      dataLabels: {
        filter: {
          property: 'y',
          operator: '>',
          value: null,
        },
      },
    },
    bar: {
      groupPadding: 0.09,
      dataLabels: {
        filter: {
          property: 'y',
          operator: '>',
          value: null,
        },
      },
    },
  },
  tooltip: {
    borderWidth: 0,
    shadow: false,
    useHTML: true,
  },
};

const YAXIS = {
  title: null,
  labels: {
    style: {
      color: '#333333',
    },
  },
  tickColor: 'rgba(158, 158, 158, 0.5)',
};

@Component({
  selector: 'app-chart',
  template: `
    <div class="chart-wrapper" [class.has-footer]="!!footer">
      <div #containerRef class="chart-container"></div>
      <div #footerRef *ngIf="!!footer" class="chart-footer">
        <div *ngIf="loadingBenchmarks" class="benchmark-loader">Loading {{comparisonPeriod}} data...</div>

        <span *ngIf="footer.datasetInfo">{{footerData.datasetInfo}}</span>
        <span *ngIf="footer.dateRange">{{footerData.dateRange}}</span>
      </div>
    </div>
  `,
  styleUrls: ['chart.component.less'],

  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent implements AfterViewInit, OnChanges {
  @Input() series: any[];
  @Input() highchartsOptions: any;
  @Input() chartOptions: ChartOptions = {};
  @Input() style: WidgetStyle;
  @Input() datasetDisplayName: string;
  @Input() footer: any;
  @Input() footerData: any;
  @Input() comparisonSeries: any[] = [];
  @Input() comparisonPeriod: string;
  @Input() loadingBenchmarks: boolean = false;
  @Input() widgetId: string;
  @Input() queryFirstDayOfWeek: string;

  @ViewChild('containerRef') containerRef: ElementRef;
  @ViewChildren('footerRef') footerRef: QueryList<any>;
  instance: any;
  window = <any>window;
  formatter: any;
  currencyPipe: any;
  backgroundColor: string;

  constructor(@Inject(LOCALE_ID) locale: string, session: SessionService, private datesService: DatesService, private colorService: ColorService, private renderer: Renderer2, private widgetService: WidgetService) {
    this.formatter = new FormatPipe(locale, session);
    this.currencyPipe = new CurrencyPipe(locale, session);
    this.widgetService.zoomed.subscribe(() => {
      if (this.instance) {
        setTimeout(() => {
          this.drawChart(false);
        });
      }
    });
  }

  ngAfterViewInit() {
    this.drawChart(true);
    $(this.window).resize(() => {
      setTimeout(() => {
        this.instance.reflow();
      });
    });
  }

  ngOnChanges(changes) {
    if (this.instance) {
      let animate = changes.highchartsOptions as boolean;
      if (changes.series) {
        const previous = changes.series?.previousValue?.data;
        const current = changes.series?.currentValue?.data;
        animate = JSON.stringify(previous) !== JSON.stringify(current);
      } else if (changes.comparisonSeries) {
        const previous = changes.comparisonSeries?.previousValue?.data;
        const current = changes.comparisonSeries?.currentValue?.data;
        animate = JSON.stringify(previous) !== JSON.stringify(current);
      }
      this.drawChart(animate);
    }
  }

  private drawChart(animate = false) {
    const baseOptions: Partial<Options> = { colors: this.colorService.defaultColors };
    let options = merge(baseOptions, HIGHCHARTS_OPTIONS, { deep: true }) as Options;
    options = merge(options, this.highchartsOptions, { deep: true }) as Options;

    if (this.chartOptions?.yAxis) {
      if (this.chartOptions.yAxis.sharedAxis && this.hasMixedDataTypes()) {
        this.chartOptions.yAxis.sharedAxis = false;
      }

      if (this.chartOptions.yAxis.normalizeYAxis && this.hasMixedDataTypes()) {
        this.chartOptions.yAxis.normalizeYAxis = false;
      }
    }

    this.setHighchartsOptionsFromChartOptions(options);
    this.setYAxisFormatter(options);
    this.setXAxisFormatter(options);
    options = merge(options, { series: this.ensureNullValues(this.series) }, { deep: true }) as Options;

    if (this.comparisonSeries?.length) {
      this.ensureNullValues(this.comparisonSeries).forEach(comparisonSeries => {
        options['series'].push(comparisonSeries as SeriesOptionsType);
      });
    } else {
      if (options['series'].length > this.series.length) {
        options['series'] = this.ensureNullValues(this.series);
      }
    }

    options['tooltip']['shared'] = !this.chartOptions.chartTooltip?.singleValueOnHover;
    this.setTooltipFormatter(options);

    if (this.widgetId && this.style?.enableColorPerChartValue) {
      const mergedColors = this.getColorsForChart(options['series']);
      if (mergedColors.length) {
        options.colors = mergedColors.map(colorItem => colorItem.color);
        this.colorService.setChartValueColors(this.widgetId, mergedColors);
      }
    }

    if (animate) {
      options['chart'].animation = true;
      options['plotOptions'].series.animation = true;
    }
    this.instance = Highcharts.chart(this.containerRef.nativeElement, options);
  }

  private hasMixedDataTypes(): boolean {
    if (!this.series || this.series.length <= 1) {
      return false;
    }

    const formats = this.series.map(s => {
      if (s.format && typeof s.format === 'object' && 'format' in s.format) {
        return s.format.format || 'number';
      }
      if (typeof s.format === 'string') {
        return s.format;
      }
      return 'number';
    });

    const uniqueFormats = [...new Set(formats)];
    return uniqueFormats.length > 1;
  }

  private getColorsForChart(series): ChartValueColor[] {
    const existingAndNewColors: ChartValueColor[] = [];
    const usedColors = new Set<string>();
    const seriesData = this.highchartsOptions.chart.type === 'pie' ? series[0]?.data : series;

    seriesData.forEach(serieOrPoint => {
      const styleColor = this.style?.colorPerChartValues?.find(item => item.value === serieOrPoint.name);
      let colorToUse: string;

      if (this.style?.enableColorPerChartValue && styleColor?.color && !usedColors.has(styleColor.color)) {
        colorToUse = styleColor.color;
      } else {
        colorToUse = this.getNextAvailableColor(usedColors);
      }

      usedColors.add(colorToUse);
      existingAndNewColors.push({
        value: serieOrPoint.name,
        color: colorToUse,
      });
    });

    return existingAndNewColors;
  }

  private getNextAvailableColor(usedColors: Set<string>): string {
    const defaultColors = this.colorService.defaultColors;

    for (const color of defaultColors) {
      if (!usedColors.has(color)) {
        return color;
      }
    }

    return defaultColors[0];
  }

  private setYAxisLabels(options) {
    if (!this.chartOptions.yAxis?.showLabels) {
      return;
    }

    const seriesByAxis = new Map<number, string[]>();

    this.series.forEach(serie => {
      const yAxisIndex = serie.yAxis || 0;
      if (!seriesByAxis[yAxisIndex]) {
        seriesByAxis[yAxisIndex] = [];
      }

      const metricName = serie.displayName ||
        serie.originalName
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

      if (metricName && !seriesByAxis[yAxisIndex].includes(metricName)) {
        seriesByAxis[yAxisIndex].push(metricName);
      }
    });

    options.yAxis.forEach((axis, index) => {
      const metrics = seriesByAxis[index];
      if (metrics && metrics.length > 0) {
        axis.title = {
          text: metrics.join(', '),
          style: {
            color: this.chartOptions.textColor || '#333333',
            fontSize: (this.chartOptions.yAxis.size_left || DEFAULT_AXIS_FONT_SIZE) + 'px',
          },
        };
      }
    });
  }

  private setHighchartsOptionsFromChartOptions(options) {
    const co = this.chartOptions;
    if (co) {
      if (this.footer?.datasetInfo || this.footer?.dateRange) {
        const borderWidth = this.style?.borderWidth || 0;
        const adjustedHeight = borderWidth > 0 ?
          `calc(90% - ${borderWidth * 2}px)` : '90%';
        this.renderer.setStyle(this.containerRef.nativeElement, 'height', adjustedHeight);
      } else {
        this.renderer.setStyle(this.containerRef.nativeElement, 'height', '100%');
      }

      if (co.colors) {
        options.colors = co.colors;
      }

      if (co.backgroundColor) {
        options.chart.backgroundColor = co.backgroundColor;
        this.backgroundColor = co.backgroundColor;
      }

      if (co.textColor) {
        for (const axis of options.xAxis) {
          axis.labels.style.color = co.textColor;
        }
        for (const axis of options.yAxis) {
          axis.labels.style.color = co.textColor;
        }
        options.legend.itemStyle.color = co.textColor;
      }

      if (co.title) {
        options.title = merge(options.title, {
          align: co.title.align || 'center',
          text: co.title.text,
          floating: false,
          style: {
            color: co.textColor,
            fontSize: (co.title.size || DEFAULT_TITLE_FONT_SIZE) + 'px',
            fontWeight: 'bold',
          },
        }, { deep: true });
      }

      if (co.dataLabels) {
        options.plotOptions.series.dataLabels = {
          enabled: co.dataLabels.rotation && co.dataLabels.rotation !== 'disabled' || co.dataLabels['enabled'],
          padding: 0,
          style: {
            color: co.textColor || '#333333',
            fontSize: (co.dataLabels.size || DEFAULT_DATA_LABEL_FONT_SIZE) + 'px',
            fontWeight: 'bold',
          },
        };
        let dataLabels = options.plotOptions.series.dataLabels;
        switch (co.dataLabels.rotation) {
          case 'horizontal': {
            dataLabels.rotation = 0;
            dataLabels.align = co.dataLabels.align || 'center';
            dataLabels.overflow = 'justify';
            dataLabels.crop = false;
            break;
          }
          case 'tilted': {
            dataLabels.rotation = -45;
            dataLabels.align = co.dataLabels.align || 'left';
            if (dataLabels.align === 'left') {
              dataLabels.y = -10;
            } else if (dataLabels.align === 'right') {
              dataLabels.y = 10;
            } else {
              dataLabels.y = 0;
            }
            dataLabels.overflow = 'none';
            dataLabels.crop = false;
            break;
          }
          case 'vertical': {
            dataLabels.rotation = -90;
            dataLabels.align = co.dataLabels.align || 'left';
            if (dataLabels.align === 'left') {
              dataLabels.y = -10;
            } else if (dataLabels.align === 'right') {
              dataLabels.y = 10;
            } else {
              dataLabels.y = 0;
            }
            dataLabels.overflow = 'none';
            dataLabels.crop = false;
            break;
          }
        }
        if (this.highchartsOptions.chart.type === 'pie') {
          dataLabels.distance = -30;
        }


        const chartComponent = this;
        if (co.stacking !== 'percent') {
          options.plotOptions.series.dataLabels.formatter = function () {
            return chartComponent.formatter.transform(this.y, this.series?.userOptions?.format || 'number');
          };
        } else {
          options.plotOptions.series.dataLabels.formatter = function () {
            return chartComponent.formatter.transform(this.percentage / 100, 'percent');
          };
        }
      }

      if (co.legend) {
        options.legend.align = 'center';
        options.legend.verticalAlign = 'top';
        options.legend.layout = 'horizontal';
        const size = parseInt(co.legend.size) || 14;
        options.legend = merge(options.legend, {
          enabled: (co.legend.layout || 'horizontal') !== 'disabled' && (co.legend['enabled'] !== undefined ? co.legend['enabled'] : true),
          align: co.legend.align || 'center',
          verticalAlign: co.legend.position || co.legend['legendPosition'] || 'bottom',
          layout: co.legend.layout || 'horizontal',
          symbolRadius: size * .75,
          symbolHeight: size * .75,
          symbolWidth: size * .75,
          itemStyle: {
            fontSize: (co.legend.size || 14) + 'px',
            color: co.textColor || '#333333',
          },
        }, { deep: true });
      }

      if (co.stacking !== 'percent') {
        const formats = uniq((this.series || []).map(s => s.format || 'number'));
        options.yAxis = (formats || []).map((format, i) => merge({
          opposite: false,
          format: format,
          labels: {
            enabled: true,
          },
          title: null,
        }, merge(YAXIS, options.yAxis[i], { deep: true }), { deep: true }));
        this.series.forEach(serie => {
          const formatAxis = formats.findIndex(f => (serie.format || 'number') === f);
          serie['yAxis'] = formatAxis !== -1 ? formatAxis : 0;
        });
      }

      if (co.yAxis) {
        options.plotOptions.column.dataLabels.filter.value = Math.max(co.yAxis['min_left'], co.yAxis['min_right']);
        options.plotOptions.bar.dataLabels.filter.value = Math.max(co.yAxis['min_left'], co.yAxis['min_right']);

        if (options.yAxis.length) {
          const uniqueMetrics = uniq(this.series.map(serie => {
            return { metric: serie.originalName, direction: co.yAxis['axis_' + serie.originalName], yAxis: serie.yAxis };
          }));

          this.recalculateYaxises(uniqueMetrics, options.yAxis.length - 1);

          if (co.yAxis?.sharedAxis) {
            const leftMetrics = this.series.filter(serie => {
              const foundMetric = uniqueMetrics.find(m => m.metric === serie.originalName);
              return foundMetric && foundMetric.direction === 'left';
            });

            const rightMetrics = this.series.filter(serie => {
              const foundMetric = uniqueMetrics.find(m => m.metric === serie.originalName);
              return foundMetric && foundMetric.direction === 'right';
            });

            let globalMin = null;
            let globalMax = null;
            if (co.yAxis['normalizeYAxis']) {
              const allSeriesData = [...this.series, ...this.comparisonSeries];
              const allValues = allSeriesData.flatMap(serie =>
                serie.data.map(point => Array.isArray(point) ? point[1] : point.y).filter(val => val !== null),
              );
              if (allValues.length > 0) {
                globalMin = 0;
                globalMax = Math.max(...allValues);
                globalMax = globalMax * 1.1;
              }
            }

            const newYAxis = [];

            if (leftMetrics.length > 0) {
              const leftMetric = this.series.find(s => s.originalName === leftMetrics[0]?.originalName);
              const leftAxisConfig: any = merge({
                labels: { enabled: true },
              }, merge(cloneDeep(YAXIS), options.yAxis[0], { deep: true }), { deep: true });

              leftAxisConfig.format = (typeof leftMetric?.format === 'object' ? leftMetric?.format?.format : leftMetric?.format) || 'number';
              leftAxisConfig.opposite = false;

              if (co.yAxis['normalizeYAxis'] && globalMin !== null && globalMax !== null) {
                leftAxisConfig.min = globalMin;
                leftAxisConfig.max = globalMax;
                leftAxisConfig.startOnTick = true;
                leftAxisConfig.endOnTick = true;
              } else {
                if (co.yAxis['min_left'] && co.yAxis['min_left'] > 0) {
                  leftAxisConfig.min = co.yAxis['min_left'];
                  leftAxisConfig.startOnTick = false;
                  leftAxisConfig.minPadding = 0;
                }
                if (co.yAxis['max_left'] && co.yAxis['max_left'] > 0) {
                  leftAxisConfig.max = co.yAxis['max_left'];
                  leftAxisConfig.endOnTick = false;
                  leftAxisConfig.maxPadding = 0;
                }
              }

              newYAxis.push(leftAxisConfig);

              leftMetrics.forEach(serie => {
                this.series.forEach((s, index) => {
                  if (s.originalName === serie.originalName) {
                    this.series[index]['yAxis'] = 0;
                  }
                });
              });
              this.comparisonSeries.forEach((s, index) => {
                const foundMetric = uniqueMetrics.find(m => m.metric === s.originalName);
                if (foundMetric && foundMetric.direction === 'left') {
                  this.comparisonSeries[index]['yAxis'] = 0;
                }
              });
            }

            if (rightMetrics.length > 0) {
              const rightAxisIndex = leftMetrics.length > 0 ? 1 : 0;
              const rightMetric = this.series.find(s => s.originalName === rightMetrics[0]?.originalName);
              const rightAxisConfig: any = merge(
                { labels: { enabled: true } },
                merge(cloneDeep(YAXIS), options.yAxis.find(axis => axis.opposite === true) || options.yAxis[1] || options.yAxis[0], { deep: true }),
                { deep: true },
              );

              rightAxisConfig.format = (typeof rightMetric?.format === 'object' ? rightMetric?.format?.format : rightMetric?.format) || 'number';
              rightAxisConfig.opposite = true;

              if (co.yAxis['normalizeYAxis'] && globalMin !== null && globalMax !== null) {
                rightAxisConfig.min = globalMin;
                rightAxisConfig.max = globalMax;
                rightAxisConfig.startOnTick = true;
                rightAxisConfig.endOnTick = true;
              } else {
                if (co.yAxis['min_right'] && co.yAxis['min_right'] > 0) {
                  rightAxisConfig.min = co.yAxis['min_right'];
                  rightAxisConfig.startOnTick = false;
                  rightAxisConfig.minPadding = 0;
                }
                if (co.yAxis['max_right'] && co.yAxis['max_right'] > 0) {
                  rightAxisConfig.max = co.yAxis['max_right'];
                  rightAxisConfig.endOnTick = false;
                  rightAxisConfig.maxPadding = 0;
                }
              }

              newYAxis.push(rightAxisConfig);

              rightMetrics.forEach(serie => {
                this.series.forEach((s, index) => {
                  if (s.originalName === serie.originalName) {
                    this.series[index]['yAxis'] = rightAxisIndex;
                  }
                });
              });

              this.comparisonSeries.forEach((s, index) => {
                const foundMetric = uniqueMetrics.find(m => m.metric === s.originalName);
                if (foundMetric && foundMetric.direction === 'right') {
                  this.comparisonSeries[index]['yAxis'] = rightAxisIndex;
                }
              });
            }
            options.yAxis = newYAxis;
            this.setYAxisFormatter(options);
          } else {
            let leftAxisHasMinMax = false;
            let rightAxisHasMinMax = false;

            let globalMin = null;
            let globalMax = null;
            if (co.yAxis['normalizeYAxis']) {
              const allSeriesData = [...this.series, ...this.comparisonSeries];
              const allValues = allSeriesData.flatMap(serie =>
                serie.data.map(point => Array.isArray(point) ? point[1] : point.y).filter(val => val !== null),
              );
              if (allValues.length > 0) {
                globalMin = 0;
                globalMax = Math.max(...allValues);
                globalMax = globalMax * 1.1;
              }
            }

            for (let serie of this.series.concat(this.comparisonSeries)) {
              const foundMetric = uniqueMetrics.find(m => m.metric === serie.originalName);
              if (foundMetric) {
                if (!options.yAxis[foundMetric.yAxis]) {
                  options.yAxis[foundMetric.yAxis] = merge({
                    opposite: foundMetric.direction === 'right',
                    format: serie.format || 'number',
                    labels: {
                      enabled: true,
                    },
                  }, YAXIS, { deep: true });
                } else {
                  options.yAxis[foundMetric.yAxis].opposite = foundMetric.direction === 'right';
                }
                serie['yAxis'] = foundMetric.yAxis;

                if (co.yAxis['normalizeYAxis'] && globalMin !== null && globalMax !== null) {
                  options.yAxis[foundMetric.yAxis].min = globalMin;
                  options.yAxis[foundMetric.yAxis].max = globalMax;
                  options.yAxis[foundMetric.yAxis].startOnTick = true;
                  options.yAxis[foundMetric.yAxis].endOnTick = true;
                } else {
                  if (foundMetric.direction === 'left') {
                    if (!leftAxisHasMinMax) {
                      if (co.yAxis['min_left'] && co.yAxis['min_left'] > 0) {
                        options.yAxis[foundMetric.yAxis].min = co.yAxis['min_left'];
                        options.yAxis[foundMetric.yAxis].startOnTick = false;
                        options.yAxis[foundMetric.yAxis].minPadding = 0;
                        leftAxisHasMinMax = true;
                      }
                      if (co.yAxis['max_left'] && co.yAxis['max_left'] > 0) {
                        options.yAxis[foundMetric.yAxis].max = co.yAxis['max_left'];
                        options.yAxis[foundMetric.yAxis].endOnTick = false;
                        options.yAxis[foundMetric.yAxis].maxPadding = 0;
                        leftAxisHasMinMax = true;
                      }
                    }
                  } else {
                    if (!rightAxisHasMinMax) {
                      if (co.yAxis['min_right'] && co.yAxis['min_right'] > 0) {
                        options.yAxis[foundMetric.yAxis].min = co.yAxis['min_right'];
                        options.yAxis[foundMetric.yAxis].startOnTick = false;
                        options.yAxis[foundMetric.yAxis].minPadding = 0;
                        rightAxisHasMinMax = true;
                      }
                      if (co.yAxis['max_right'] && co.yAxis['max_right'] > 0) {
                        options.yAxis[foundMetric.yAxis].max = co.yAxis['max_right'];
                        options.yAxis[foundMetric.yAxis].endOnTick = false;
                        options.yAxis[foundMetric.yAxis].maxPadding = 0;
                        rightAxisHasMinMax = true;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      this.setYAxisLabels(options);

      if (co.hideXAxis) {
        for (const axis of options.xAxis) {
          axis['visible'] = false;
        }
      } else {
        options.xAxis[0].labels.style.fontSize = co?.xAxis?.size || DEFAULT_AXIS_FONT_SIZE;
      }

      if (co.hideYAxis) {
        for (const axis of options.yAxis) {
          axis['visible'] = false;
        }
      } else {
        options.yAxis.map(axis => {
          if (axis.opposite) {
            return axis.labels.style.fontSize = co?.yAxis?.size_right || DEFAULT_AXIS_FONT_SIZE;
          } else {
            return axis.labels.style.fontSize = co?.yAxis?.size_left || DEFAULT_AXIS_FONT_SIZE;
          }
        });
      }
    }
  }

  private setYAxisFormatter(options) {
    const chartComponent = this;
    for (const i in options['yAxis']) {
      options['yAxis'][i]['labels']['formatter'] = function () {
        let format = this.axis.options && this.axis.options.format;
        if (chartComponent.chartOptions.stacking === 'percent') {
          this.value /= 100;
          format = 'percent';
        }
        if (format === 'currency') {
          const currencySymbol = chartComponent.currencyPipe.transform(0).replace(/[0-9 ,.]/g, '');
          let formattedCurrencyValue = Highcharts.numberFormat(this.value, -1);
          return currencySymbol + formattedCurrencyValue;
        } else if (format !== 'number') {
          return chartComponent.formatter.transform(this.value, format);
        } else {
          return Highcharts.numberFormat(this.value, -1);
        }
      };
    }
  }

  private setXAxisFormatter(options) {
    const queryFirstDayOfWeek = this.queryFirstDayOfWeek ? Number(this.queryFirstDayOfWeek) : undefined;
    const chartComponent = this;
    const co = this.chartOptions;
    options['xAxis'].forEach((_, i) => {
      options['xAxis'][i].startOfWeek = queryFirstDayOfWeek || this.datesService.accountFirstDayOfWeek;
      options['xAxis'][i]['labels']['formatter'] = function () {
        return chartComponent.formatDate(co.dateType, this.value);
      };
    });
  }

  private setTooltipFormatter(options) {
    const chartComponent = this;
    options['tooltip']['formatter'] = function () {
      let html = `
        <table>
      `;

      if (this.points) {
        html += `
          <tr>
            <td valign="bottom"><span style="color:#666666; font-size:14px; font-weight:500;" colspan="2">${chartComponent.formatDate(chartComponent.chartOptions.dateType, this.x)}</span></td>
          </tr>
        `;

        for (const point of this.points) {
          const series = point['series'];
          const value = chartComponent.chartOptions.stacking === 'percent' && point.percentage ? (point.percentage / 100) : point.y;
          const format = chartComponent.chartOptions.stacking === 'percent' && point.percentage ? 'percent' : (series.userOptions?.format || 'number');

          html += `
            <tr>
              <td style="font-size:12px; font-weight:500;"><span style="color:${series['color']}">●</span> ${series['name']}:&nbsp;&nbsp;</td>
              <td style="font-size:12px; font-weight:500;" align="right">${chartComponent.formatter.transform(value, format)}</td>
            </tr>
          `;
        }
      } else {
        if (this.percentage) {
          const header = this.point.name || this.point.series.name;
          const pointName = this.point.options.key || this.point.series.name;
          const value = chartComponent.chartOptions.stacking === 'normal' ? this.point.y : (this.percentage / 100);
          const format = chartComponent.chartOptions.stacking === 'normal' ? (this.point.series.userOptions?.format || 'number') : 'percent';

          html += `
          <tr>
            <td valign="bottom" colspan="2"><span style="color:#666666; font-size:14px; font-weight:500;" colspan="2">${header}</span></td>
          </tr>
          <tr>
            <td style="font-size:12px; font-weight:500;"><span style="color:${this.color}">●</span> ${pointName}:&nbsp;&nbsp;</td>
            <td style="font-size:12px; font-weight:500;" align="right">${chartComponent.formatter.transform(value, format)}</td>
          </tr>
        `;
        } else {
          html += `
          <tr>
            <td style="font-size:12px; font-weight:500;"><span style="color:${this.color}">●</span> ${this.point.series.name}:&nbsp;&nbsp;</td>
            <td style="font-size:12px; font-weight:500;" align="right">${chartComponent.formatter.transform(this.point.y, this.point.series.userOptions?.format || 'number')}</td>
          </tr>
        `;
        }
      }

      html += '</table>';
      return html;
    };
  }

  private formatDate(dateType: string, value: any) {
    const date = new Date(value);
    switch (dateType) {
      case 'year':
        return formatInTimeZone(date, 'UTC', 'yyyy');
      case 'quarter':
        return `${date.getUTCFullYear()} Q${date.getUTCMonth() / 3 + 1}`;
      case 'month':
        return formatInTimeZone(date, 'UTC', 'MMMM');
      case 'week':
        const weekStart = date;
        const weekEnd = addDays(date, 6);
        return `${formatInTimeZone(weekStart, 'UTC', 'MMM d')} - ${formatInTimeZone(weekEnd, 'UTC', 'MMM d')}`;
      case 'day':
        return formatInTimeZone(date, 'UTC', 'MMM d');
      default:
        return formatInTimeZone(date, 'UTC', 'MMM d');
    }
  }

  private ensureNullValues(series) {
    const newSeries = series.map(s => {
      const newData = s.data.map(d => {
        if (isPlainObject(d)) {
          return merge(d, { y: d.y || 0 });
        } else if (isArray(d)) {
          return d.map(_d => _d || 0);
        } else {
          return d || 0;
        }
      });
      return merge(s, { data: newData });
    });
    return newSeries;
  }

  private recalculateYaxises(uniqueMetrics, highestYaxisIndex) {
    let metricYaxisDirections = [];
    uniqueMetrics.forEach(uniqueMetric => {
      let foundAxis = metricYaxisDirections.find(metric => metric.yAxis === uniqueMetric.yAxis);
      if (foundAxis && foundAxis.direction !== uniqueMetric.direction) {
        uniqueMetric.yAxis = highestYaxisIndex + 1;
        highestYaxisIndex++;
      }
      metricYaxisDirections.push(uniqueMetric);
    });
  }
}
